import React, { useMemo } from 'react'
import DownloadIcon from '@mui/icons-material/Download'
import FileIcon from '@mui/icons-material/InsertDriveFile'
import { ListItemAvatar } from '@mui/material'
import { useIntl } from 'react-intl'
import Dialog, { type DialogProps } from './Dialog'
import { Button } from '../Button'
import { getErrorMessage } from '../../services/helper'
import { useLoanContext } from '../../contexts/LoanContext'
import { useAlert } from '../../hooks'
import { LayoutRow } from '../layout'
import { getLoanDocumentContentUrl } from '../../services/client'
import IconButton from '../Button/IconButton'
import ImagePlaceholderPreview from '../ImagePlaceholderPreview'
import useImageValidator from '../../hooks/useImageValidator'
import { MediaFileProps } from '../MediaLibrary/types'

export type ModalDownloadDocumentsProps = {
	documents: MediaFileProps[]
	onSubmit: () => void
	onCancel?: () => void
} & Omit<DialogProps, 'onSubmit'>
const ModalDownloadDocuments = ({
	documents = [],
	onSubmit,
	onCancel = undefined,
	...rest
}: ModalDownloadDocumentsProps) => {
	const { loanId } = useLoanContext()
	const { alert } = useAlert()
	const isImage = useImageValidator()
	const { formatMessage } = useIntl()

	const mutatedDocuments = useMemo(
		() =>
			documents.map((doc) => ({
				...doc,
				url:
					doc.url ??
					getLoanDocumentContentUrl({
						loanId,
						documentId: doc.id,
					}) ??
					undefined,
			})) ?? [],
		[documents, loanId]
	)

	if (mutatedDocuments.length === 0 || !mutatedDocuments[0]?.url)
		return null

	const downloadDocument = (url: string) => {
		const a = document.createElement('a')
		a.href = url
		a.download = 'document'
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
	}

	const handleDownloadClick = (documentId: string) => {
		const document = mutatedDocuments.find(
			(doc) => doc.id === documentId
		)
		if (!document?.url) return
		downloadDocument(document.url)
	}

	const handleDownloadAllClick = () => {
		try {
			mutatedDocuments.forEach((doc) =>
				doc?.url ? downloadDocument(doc.url) : null
			)
			onSubmit()
		} catch (error) {
			alert(getErrorMessage(error), { severity: 'error' })
		}
	}

	return (
		<Dialog
			title={formatMessage({
				id: 'loan_documents.download_modal.heading',
			})}
			onClose={onCancel}
			fullWidth
			maxWidth="sm"
			{...rest}
		>
			<LayoutRow>
				<ul className="flex flex-col gap-2">
					{mutatedDocuments.map((doc) => (
						<li
							key={doc.id}
							className="py-1 flex items-center justify-between"
						>
							<span className="flex items-center">
								<ListItemAvatar>
									{isImage({ path: doc.fileName }) ? (
										<ImagePlaceholderPreview
											file={{
												file: {
													url: doc.url,
													fileName: doc.name,
												},
											}}
										/>
									) : (
										<FileIcon />
									)}
								</ListItemAvatar>
								<span>{doc.name}</span>
							</span>
							<IconButton>
								<DownloadIcon
									onClick={() => handleDownloadClick(doc.id)}
								/>
							</IconButton>
						</li>
					))}
				</ul>
			</LayoutRow>
			<LayoutRow className="flex justify-end">
				<Button
					text={formatMessage({
						id: 'loan_documents.download_modal.download_all',
					})}
					onClick={handleDownloadAllClick}
					endIcon={<DownloadIcon />}
				/>
			</LayoutRow>
		</Dialog>
	)
}

export default ModalDownloadDocuments
